import styled from "styled-components";
import { theme } from "../../../theme";
import Subheading from "../../atoms/typography/Subheading";
import BodySmall from "../../atoms/typography/BodySmall";
import DonutChart from "../DonutChart";
import { CircleActivityIndicator } from "../../atoms/CircleActivityIndicator";
import { Row } from "react-grid-system";
import { OpenLinkIcon } from "../../atoms/icons/OpenLinkIcon";
import { HorizontalSpacer, VerticalSpacer } from "../../atoms/Spacers";

type DataWithColor = {
  id: string;
  titleKey: string;
  percentage: number;
  valueText: string;
  color: string;
};

type DataWithoutColor = {
  id: string;
  titleKey: string;
  percentage: number;
  valueText: string;
  color?: never;
};

interface BaseProps {
  title: string;
  subtitle?: string | React.ReactNode;
  link?: string;
}

interface StatusProps extends BaseProps {
  colorType: "status";
  data: DataWithColor[];
}

interface NonStatusProps extends BaseProps {
  colorType: "turqoise" | "ocean" | "midnight";
  data: DataWithoutColor[];
}

type Props = StatusProps | NonStatusProps;

const Container = styled.div`
  padding: 16px;
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
`;

const FlexedDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DonutStatSection: React.FC<Props> = ({
  data,
  colorType,
  title,
  subtitle,
  link,
}) => {
  const turqoiseColors = ["197A80", "0F5156", "7A8E8F", "4B8084"];
  const oceanColors = ["0F5A78", "1A87A0", "7B949A", "1481AD"];
  const midnightColors = [
    "0F3B57",
    "156076",
    "6A96A6",
    "7C8D95",
    "0F3B57",
    "1C3A5F",
    "244D58",
  ];

  const appendColorPropertyToData = () => {
    return data.map((entry, index) => {
      let color = "";
      switch (colorType) {
        case "turqoise":
          color = `#${turqoiseColors[index % turqoiseColors.length]}`;
          break;
        case "ocean":
          color = `#${oceanColors[index % oceanColors.length]}`;
          break;
        case "midnight":
          color = `#${midnightColors[index % midnightColors.length]}`;
          break;
        case "status":
          color = entry.color!;
          break;
        default:
          color = "#000000"; // Default color if type is unrecognized
      }
      return { ...entry, color };
    });
  };

  const myData = appendColorPropertyToData();

  return (
    <Container>
      <Row nogutter justify="between">
        <Subheading color={theme.colors.primary}>{title}</Subheading>
        {link && <OpenLinkIcon color={theme.colors.primary} />}
      </Row>
      {subtitle && (
        <BodySmall color={theme.colors.textMuted}>{subtitle}</BodySmall>
      )}
      <HorizontalSpacer spacing={0.5} />
      <FlexedDiv>
        <DonutChart data={myData} />
        <VerticalSpacer spacing={1} />
        <div style={{ flex: 1 }}>
          {myData.map((entry) => (
            <Row
              nogutter
              align="center"
              justify="between"
              key={entry.id}
              style={{ marginTop: 4, marginBottom: 4 }}
            >
              <FlexedDiv>
                <CircleActivityIndicator color={entry.color} />
                <BodySmall>{entry.titleKey}</BodySmall>
              </FlexedDiv>
              <BodySmall fontweight={500}>{entry.valueText}</BodySmall>
            </Row>
          ))}
        </div>
      </FlexedDiv>
    </Container>
  );
};

export default DonutStatSection;
